import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  TextField,
  TextFieldProps,
  Box,
} from '@mui/material';
import MentionAutocomplete from './MentionAutocomplete';
import {
  MentionSearchResult,
  SelectedMention,
  MentionSearchContext,
  MentionContentType,
} from '../../types/mention';
import { mentionSearchService } from '../../services/mentionSearchService';
import { useAuth } from '../../contexts/AuthContext';

interface MentionInputProps extends Omit<TextFieldProps, 'onChange'> {
  value: string;
  onChange: (value: string, mentions: SelectedMention[]) => void;
  contentType: MentionContentType;
  targetId?: string;
  conversationId?: string;
  maxMentions?: number;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  contentType,
  targetId,
  conversationId,
  maxMentions = 10,
  ...textFieldProps
}) => {
  const { authState } = useAuth();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MentionSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentions, setMentions] = useState<SelectedMention[]>([]);
  const [mentionStart, setMentionStart] = useState<number | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Search context
  const searchContext = useMemo<MentionSearchContext>(() => ({
    contentType,
    targetId: targetId || conversationId,
    currentUserId: authState.user?.uid || '',
  }), [contentType, targetId, conversationId, authState.user]);

  // Handle text change
  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    const cursorPosition = event.target.selectionStart || 0;

    // Check if @ was typed
    if (newValue.length > value.length && newValue[cursorPosition - 1] === '@') {
      // Start mention mode
      setMentionStart(cursorPosition - 1);
      setShowAutocomplete(true);
      setSearchTerm('');
      setSelectedIndex(0);
    } else if (mentionStart !== null && cursorPosition > mentionStart) {
      // Update search term while in mention mode
      const term = newValue.substring(mentionStart + 1, cursorPosition);

      // Check if space or newline was typed (end mention)
      if (term.includes(' ') || term.includes('\n')) {
        setShowAutocomplete(false);
        setMentionStart(null);
      } else {
        setSearchTerm(term);
        setSelectedIndex(0);
      }
    } else if (mentionStart !== null) {
      // Cursor moved before mention start - cancel
      setShowAutocomplete(false);
      setMentionStart(null);
    }

    onChange(newValue, mentions);
  }, [value, mentionStart, onChange, mentions]);

  // Debounced search
  useEffect(() => {
    if (!showAutocomplete || !authState.user) {
      return;
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      if (searchTerm.length === 0) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const results = await mentionSearchService.searchUsersForMention(
          searchTerm,
          searchContext,
          10
        );
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, showAutocomplete, searchContext, authState.user]);

  // Handle mention selection
  const handleSelectMention = useCallback((user: MentionSearchResult) => {
    if (mentionStart === null) return;

    // Check max mentions
    if (mentions.length >= maxMentions) {
      alert(`Maximum ${maxMentions} mentions allowed`);
      setShowAutocomplete(false);
      setMentionStart(null);
      return;
    }

    // Replace search term with @Username
    const before = value.substring(0, mentionStart);
    const after = value.substring(mentionStart + 1 + searchTerm.length);
    const newValue = `${before}@${user.displayName} ${after}`;

    // Add to mentions list
    const newMention: SelectedMention = {
      userId: user.userId,
      username: user.displayName,
      position: mentionStart,
    };
    const newMentions = [...mentions, newMention];
    setMentions(newMentions);

    // Update value
    onChange(newValue, newMentions);

    // Close autocomplete
    setShowAutocomplete(false);
    setMentionStart(null);

    // Focus back on input
    if (inputRef.current) {
      const newCursorPos = mentionStart + user.displayName.length + 2; // +2 for @ and space
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  }, [mentionStart, value, searchTerm, mentions, maxMentions, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!showAutocomplete) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;

      case 'Enter':
        if (searchResults.length > 0) {
          event.preventDefault();
          handleSelectMention(searchResults[selectedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setShowAutocomplete(false);
        setMentionStart(null);
        break;
    }
  }, [showAutocomplete, searchResults, selectedIndex, handleSelectMention]);

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        multiline
      />

      {showAutocomplete && (
        <MentionAutocomplete
          searchTerm={searchTerm}
          onSelect={handleSelectMention}
          onClose={() => {
            setShowAutocomplete(false);
            setMentionStart(null);
          }}
          results={searchResults}
          loading={loading}
          selectedIndex={selectedIndex}
        />
      )}
    </Box>
  );
};

export default MentionInput;
